import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, AlertCircle, Info } from 'lucide-react';
interface TooltipItem {
  name: string;
  badge: string;
  badgeColor: string;
  bgColor: string;
  borderColor: string;
  tooltip: string;
}
export function IngredientDemo() {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const avoidItems: TooltipItem[] = [{
    name: 'Red 40, Yellow 5',
    badge: 'AVOID',
    badgeColor: 'bg-red-100 text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-100',
    tooltip: 'Artificial colors linked to hyperactivity in children and potential carcinogenic effects. Banned in several European countries.'
  }, {
    name: 'Sodium Benzoate',
    badge: 'AVOID',
    badgeColor: 'bg-red-100 text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-100',
    tooltip: 'A preservative that can form benzene (a carcinogen) when combined with vitamin C. May trigger allergic reactions.'
  }];
  const limitItems: TooltipItem[] = [{
    name: 'Sugar, Corn Syrup',
    badge: 'LIMIT',
    badgeColor: 'bg-accent-100 text-accent-700',
    bgColor: 'bg-accent-50',
    borderColor: 'border-accent-100',
    tooltip: 'Excessive sugar intake is linked to obesity, type 2 diabetes, and heart disease. Corn syrup is a highly processed sweetener.'
  }];
  const safeItems: TooltipItem[] = [{
    name: 'Water, Citric Acid, Natural Flavors',
    badge: 'SAFE',
    badgeColor: 'bg-brand-100 text-brand-700',
    bgColor: 'bg-brand-50',
    borderColor: 'border-brand-100',
    tooltip: 'These are generally recognized as safe (GRAS). Citric acid is naturally found in citrus fruits. Natural flavors are derived from plant or animal sources.'
  }];
  const allItems = [...avoidItems, ...limitItems, ...safeItems];
  return <section className="py-20 bg-white overflow-hidden" data-id="element-457">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-458">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-459">
          {/* Left: Text */}
          <div className="order-2 lg:order-1" data-id="element-460">
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-6" data-id="element-461">
              See It In Action
            </h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-462">
              IntelliEats breaks down complex ingredient lists into simple,
              actionable insights. Hover over each result to learn why it
              matters.
            </p>

            <div className="space-y-6" data-id="element-463">
              <div className="flex items-start gap-4" data-id="element-464">
                <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center shrink-0" data-id="element-465">
                  <Check className="w-5 h-5 text-brand-600" data-id="element-466" />
                </div>
                <div data-id="element-467">
                  <h3 className="font-bold text-slate-900 text-lg" data-id="element-468">
                    Safe Ingredients
                  </h3>
                  <p className="text-slate-600" data-id="element-469">
                    Green indicators for natural, healthy ingredients that are
                    safe to consume.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-470">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center shrink-0" data-id="element-471">
                  <AlertCircle className="w-5 h-5 text-accent-600" data-id="element-472" />
                </div>
                <div data-id="element-473">
                  <h3 className="font-bold text-slate-900 text-lg" data-id="element-474">
                    Limited Intake
                  </h3>
                  <p className="text-slate-600" data-id="element-475">
                    Amber warnings for ingredients like sugar or sodium that
                    should be moderated.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-476">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0" data-id="element-477">
                  <X className="w-5 h-5 text-red-600" data-id="element-478" />
                </div>
                <div data-id="element-479">
                  <h3 className="font-bold text-slate-900 text-lg" data-id="element-480">
                    Ingredients to Avoid
                  </h3>
                  <p className="text-slate-600" data-id="element-481">
                    Red alerts for harmful additives, artificial colors, and
                    preservatives.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Visual Demo */}
          <div className="order-1 lg:order-2 relative" data-id="element-482">
            <div className="absolute inset-0 bg-gradient-to-tr from-brand-100 to-accent-100 rounded-3xl transform rotate-3 scale-105 -z-10 opacity-50" data-id="element-483"></div>

            <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden" data-id="element-484">
              {/* Mock Header */}
              <div className="bg-slate-50 border-b border-slate-100 p-4 flex items-center gap-2" data-id="element-485">
                <div className="flex gap-1.5" data-id="element-486">
                  <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-487"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-488"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-489"></div>
                </div>
                <div className="ml-4 text-xs font-medium text-slate-400" data-id="element-490">
                  Analysis Result
                </div>
                <div className="ml-auto flex items-center gap-1 text-xs text-slate-400" data-id="element-491">
                  <Info className="w-3 h-3" data-id="element-492" />
                  Hover for details
                </div>
              </div>

              <div className="p-6 sm:p-8" data-id="element-493">
                <div className="mb-6" data-id="element-494">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-495">
                    Scanned Text
                  </h4>
                  <div className="p-4 bg-slate-50 rounded-xl text-sm text-slate-600 font-mono leading-relaxed border border-slate-100" data-id="element-496">
                    Water, Sugar, Corn Syrup, Artificial Colors (Red 40, Yellow
                    5), Sodium Benzoate, Citric Acid, Natural Flavors
                  </div>
                </div>

                <div data-id="element-497">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-498">
                    AI Detection
                  </h4>
                  <div className="space-y-2" data-id="element-499">
                    {allItems.map((item, index) => <motion.div key={item.name} initial={{
                    opacity: 0,
                    x: -10
                  }} whileInView={{
                    opacity: 1,
                    x: 0
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.4,
                    delay: index * 0.15
                  }} className={`relative flex items-center justify-between p-3 ${item.bgColor} border ${item.borderColor} rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5`} onMouseEnter={() => setHoveredItem(item.name)} onMouseLeave={() => setHoveredItem(null)} data-id="element-500">
                        <span className="font-medium text-slate-900" data-id="element-501">
                          {item.name}
                        </span>
                        <span className={`px-2 py-1 ${item.badgeColor} text-xs font-bold rounded shrink-0 ml-2`} data-id="element-502">
                          {item.badge}
                        </span>

                        {/* Tooltip */}
                        <AnimatePresence data-id="element-503">
                          {hoveredItem === item.name && <motion.div initial={{
                        opacity: 0,
                        y: 8,
                        scale: 0.95
                      }} animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1
                      }} exit={{
                        opacity: 0,
                        y: 8,
                        scale: 0.95
                      }} transition={{
                        duration: 0.2
                      }} className="absolute left-0 right-0 top-full mt-2 z-30 bg-slate-900 text-white text-xs rounded-xl p-3 shadow-xl leading-relaxed" data-id="element-504">
                              <div className="absolute -top-1.5 left-6 w-3 h-3 bg-slate-900 transform rotate-45" data-id="element-505"></div>
                              {item.tooltip}
                            </motion.div>}
                        </AnimatePresence>
                      </motion.div>)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}